"use client";

import React, {useEffect, useState} from "react";
import {Swiper, SwiperSlide} from "swiper/react";
import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import {Autoplay, EffectFade, Grid as SwiperGrid, Keyboard, Navigation, Pagination,} from "swiper/modules";

import "swiper/css/grid";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import {CustomSwiperProps} from "@/types/general";



export default function CustomSwiper({
                                         items,
                                         className = "",
                                         mode = "",
                                         clickable = false,
                                         grid = 1,
                                         delay = 2500,

                                         slideMinWidth = 205,
                                         slideMaxWidth = 310,
                                         slideFluidWidth = "",

                                         spaceBetween = 20,

                                         prevRef,
                                         nextRef,
                                         offset = false,
                                         loop = true,
                                         auto = true,
                                         overflowVisible = false,
                                         navigation = false,
                                     }: CustomSwiperProps) {
    const theme = useTheme();
    const [isClient, setIsClient] = useState(false);
    const isSingleMode = mode === "single";

    useEffect(() => {
        setIsClient(true);
    }, []);

    const slideWidth = `clamp(${slideMinWidth}px, ${slideFluidWidth}, ${slideMaxWidth}px)`;

    const updateNavigationButtons = (swiper: any) => {
        if (prevRef?.current) {
            prevRef.current.disabled = swiper.isBeginning;
        }

        if (nextRef?.current) {
            nextRef.current.disabled = swiper.isEnd;
        }
    };

    const swiperSettings = {
        className: `mySwiper ${className}`,
        modules: [Autoplay, Keyboard, Pagination, Navigation, EffectFade, SwiperGrid],

        dir: "rtl" as const,
        loop,
        rewind: false,

        slidesPerView: isSingleMode ? 1 : "auto",
        spaceBetween,
        slidesPerGroup: 1,
        slidesPerGroupAuto: false,

        slidesOffsetBefore: 0,
        slidesOffsetAfter: offset ? 28 : 0,

        simulateTouch: true,
        followFinger: true,
        resistance: true,
        resistanceRatio: 0.85,
        freeMode: false,
        watchSlidesProgress: true,

        speed: 600,

        grid: {
            rows: grid,
            fill: "row" as const,
        },

        navigation: navigation
            ? {
                prevEl: prevRef?.current,
                nextEl: nextRef?.current,
            }
            : undefined,

        pagination: clickable
            ? {
                clickable: true,
                dynamicBullets: true,
            }
            : false,

        autoplay: auto
            ? {
                delay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            }
            : undefined,

        onInit: updateNavigationButtons,
        onSlideChange: updateNavigationButtons,
    };

    return (
        <div
            style = {{
                height: "100%",
                width: "100%",
                overflow: overflowVisible ? "visible" : "hidden",
            }}
            className = {className}
        >
            {isClient ? (
                <Box
                    sx = {{
                        "& .swiper-wrapper": {
                            paddingTop: 4,
                            paddingBottom: clickable ? 2 : 4,
                        },

                        "& .swiper-slide": {
                            width: `${slideWidth} !important`,
                            height: "auto",
                        },

                        "& .swiper-pagination-bullet": {
                            backgroundColor: theme.palette.grey[800],
                        },

                        "& .swiper-pagination-bullet-active": {
                            backgroundColor: theme.palette.primary.main,
                        },
                    }}
                >
                    <Swiper {...swiperSettings}>
                        {items?.map((item, index) => (
                            <SwiperSlide
                                key = {index}
                                style = {
                                    isSingleMode
                                        ? {width: "100%"}
                                        : {width: slideWidth}
                                }
                            >
                                {item}
                            </SwiperSlide >
                        ))}
                    </Swiper >
                </Box >
            ) : (
                <Box
                    className = {`basic-swiper ${className}`}
                    width = "100%"
                    minWidth = "100%"
                    sx = {{
                        overflow: overflowVisible ? "visible" : "hidden",
                    }}
                >
                    <Box
                        className = "swiper-wrapper"
                        display = "flex"
                        width = "100%"
                        minWidth = "100%"
                        py = {4}
                        sx = {{
                            gap: `${spaceBetween}px`,
                        }}
                    >
                        {items?.slice(0, 6).map((item, index) => (
                            <Box
                                key = {index}
                                className = "swiper-slide"
                                sx = {
                                    isSingleMode
                                        ? {
                                            height: "auto",
                                            flex: "0 0 100%",
                                            width: "100%",
                                        }
                                        : {
                                            height: "auto",
                                            flex: "0 0 auto",
                                            width: slideWidth,
                                        }
                                }
                            >
                                {item}
                            </Box >
                        ))}
                    </Box >
                </Box >
            )}
        </div >
    );
}